import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Preview as PreviewIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useHistory, Link } from 'react-router-dom';
import pageService from './services/pageService';

const PageManagement = () => {
  const history = useHistory();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPageId, setMenuPageId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    pageName: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    const loadPages = async () => {
      try {
        setLoading(true);

        // Get current companyId from sessionStorage
        const sessionCompanyId = sessionStorage.getItem('TEMPCOM');
        let currentCompanyId = null;
        if (sessionCompanyId) {
          try {
            currentCompanyId = parseInt(JSON.parse(sessionCompanyId));
          } catch (e) {
            currentCompanyId = parseInt(sessionCompanyId);
          }
        }

        const list = await pageService.list();

        // Filter by companyId and normalize
        const filtered = currentCompanyId
          ? list.filter(p => {
              const pageCompanyId = p.companyId ?? p.CompanyId;
              return pageCompanyId === currentCompanyId;
            })
          : list;


        const normalized = filtered.map(p => ({
          id: p.id ?? p.pageId ?? p.PageId,
          pageName: p.pageName || p.name,
          description: p.description || '',
          createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
          updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
          isActive: p.isActive ?? true,
          widgetCount: p.widgetCount ?? (p.widgets?.length || 0),
          companyId: p.companyId ?? p.CompanyId,
        }));

        setPages(normalized);
      } catch (error) {
        console.error('❌ Error loading pages:', error);
        setSnackbar({
          open: true,
          message: 'Error loading pages',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadPages();
  }, []);

  const handleOpenDialog = useCallback((page = null) => {
    if (page) {
      setFormData({
        pageName: page.pageName,
        description: page.description,
        isActive: page.isActive
      });
      setSelectedPage(page);
    } else {
      setFormData({
        pageName: '',
        description: '',
        isActive: true
      });
      setSelectedPage(null);
    }
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedPage(null);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      // Prevent duplicate names (case-insensitive)
      const exists = pages.some(p => (p.pageName || '').trim().toLowerCase() === formData.pageName.trim().toLowerCase() && (!selectedPage || p.id !== selectedPage.id));
      if (exists) {
        setSnackbar({ open: true, message: 'A page with this name already exists', severity: 'warning' });
        return;
      }
      if (selectedPage) {
        // Fetch existing page data to preserve widgets and settings
        const existing = await pageService.get(selectedPage.id);
        await pageService.update(selectedPage.id, {
          pageName: formData.pageName,
          description: formData.description,
          isActive: formData.isActive,
          widgets: existing?.widgets || [],
          width: existing?.width,
          height: existing?.height,
          pageIcon: existing?.pageIcon || '',
          backgroundColor: existing?.backgroundColor,
          backgroundImage: existing?.backgroundImage,
          backgroundSize: existing?.backgroundSize,
          backgroundPosition: existing?.backgroundPosition,
          backgroundRepeat: existing?.backgroundRepeat,
        });
        setPages(prev => prev.map(p => (
          p.id === selectedPage.id
            ? { ...p, ...formData, updatedAt: new Date() }
            : p
        )));
        setSnackbar({
          open: true,
          message: 'Page updated successfully',
          severity: 'success'
        });
      } else {
        const created = await pageService.create({
          pageName: formData.pageName,
          description: formData.description,
          isActive: formData.isActive,
          widgets: [],
        });
        const normalized = {
          id: created.id,
          pageName: created.pageName,
          description: created.description,
          createdAt: created.createdAt ? new Date(created.createdAt) : new Date(),
          updatedAt: created.updatedAt ? new Date(created.updatedAt) : new Date(),
          isActive: created.isActive ?? true,
          widgetCount: created.widgets?.length || 0,
        };
        setPages(prev => [...prev, normalized]);
        setSnackbar({
          open: true,
          message: 'Page created successfully',
          severity: 'success'
        });
      }
      handleCloseDialog();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error saving page',
        severity: 'error'
      });
    }
  }, [formData, selectedPage]);

  const handleDelete = useCallback(async () => {
    try {
      await pageService.remove(selectedPage.id);
      setPages(prev => prev.filter(p => p.id !== selectedPage.id));
      setSnackbar({
        open: true,
        message: 'Page deleted successfully',
        severity: 'success'
      });
      setDeleteConfirmOpen(false);
      setSelectedPage(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error deleting page',
        severity: 'error'
      });
    }
  }, [selectedPage]);

  const handleMenuClick = useCallback((event, pageId) => {
    setAnchorEl(event.currentTarget);
    setMenuPageId(pageId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuPageId(null);
  }, []);

  const handleDesignPage = useCallback((page) => {
    const id = page?.id ?? page?.pageId ?? page?.PageId;
    if (!id) return;
    history.push(`/axisPageDesigner?id=${id}`, { pageId: id });
  }, [history]);

  const handlePreviewPage = useCallback((page) => {
    const id = page?.id ?? page?.pageId ?? page?.PageId;
    if (!id) return;
    history.push(`/axisPreview/${id}`, { pageId: id });
  }, [history]);

  const handleClonePage = useCallback(async (page) => {
    try {
      const pageId = page?.id ?? page?.pageId ?? page?.PageId;
      if (!pageId) return;

      // Fetch full page data (with widgets, canvas size, background etc.)
      const fullPage = await pageService.get(pageId);
      if (!fullPage) {
        setSnackbar({ open: true, message: 'Sayfa verisi yüklenemedi', severity: 'error' });
        return;
      }

      // Create a new page with all the data from the original
      const created = await pageService.create({
        pageName: `${fullPage.pageName} (Copy)`,
        description: fullPage.description || '',
        isActive: true,
        widgets: fullPage.widgets || [],
        width: fullPage.width,
        height: fullPage.height,
        pageIcon: fullPage.pageIcon || '',
        backgroundColor: fullPage.backgroundColor,
        backgroundImage: fullPage.backgroundImage,
        backgroundSize: fullPage.backgroundSize,
        backgroundPosition: fullPage.backgroundPosition,
        backgroundRepeat: fullPage.backgroundRepeat,
      });

      // Add to local state (same pattern as handleSave)
      const normalized = {
        id: created.id,
        pageName: created.pageName,
        description: created.description,
        createdAt: created.createdAt ? new Date(created.createdAt) : new Date(),
        updatedAt: created.updatedAt ? new Date(created.updatedAt) : new Date(),
        isActive: created.isActive ?? true,
        widgetCount: created.widgets?.length || 0,
      };
      setPages(prev => [...prev, normalized]);
      setSnackbar({
        open: true,
        message: 'Sayfa başarıyla klonlandı',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error cloning page:', error);
      setSnackbar({
        open: true,
        message: 'Sayfa klonlanırken hata oluştu',
        severity: 'error'
      });
    }
    handleMenuClose();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Sayfa Yönetimi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: 'rgb(237, 188, 69)', color: '#000', '&:hover': { bgcolor: 'rgb(220, 175, 60)' } }}
        >
          Yeni Sayfa
        </Button>
      </Box>

      {/* Pages Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sayfa Adı</TableCell>
              <TableCell>Açıklama</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Widget'lar</TableCell>
              <TableCell>Oluşturma</TableCell>
              <TableCell>Güncelleme</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pages.map((page) => (
              <TableRow key={page.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">
                    {page.pageName}
                  </Typography>
                </TableCell>
                <TableCell>{page.description}</TableCell>
                <TableCell>
                  <Chip
                    label={page.isActive ? 'Aktif' : 'Pasif'}
                    color={page.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{page.widgetCount}</TableCell>
                <TableCell>
                  {page.createdAt.toLocaleDateString()}
                </TableCell>
                <TableCell>
                  {page.updatedAt.toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/scada/designer?pageId=${page.id ?? page.pageId ?? page.PageId}`}
                    state={{ pageId: page.id ?? page.pageId ?? page.PageId }}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    component={Link}
                    to={`/axisPreview/${page.id ?? page.pageId ?? page.PageId}`}
                    state={{ pageId: page.id ?? page.pageId ?? page.PageId }}
                    color="secondary"
                  >
                    <PreviewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, page.id)}
                  >
                    <MoreIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          const page = pages.find(p => p.id === menuPageId);
          handleOpenDialog(page);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Detayları Düzenle
        </MenuItem>
        <MenuItem onClick={() => {
          const page = pages.find(p => p.id === menuPageId);
          handleClonePage(page);
        }}>
          <CopyIcon sx={{ mr: 1 }} fontSize="small" />
          Sayfayı Klonla
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setSelectedPage(pages.find(p => p.id === menuPageId));
            setDeleteConfirmOpen(true);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Sil
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedPage ? 'Sayfayı Düzenle' : 'Yeni Sayfa Oluştur'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Sayfa Adı"
            value={formData.pageName}
            onChange={(e) => setFormData(prev => ({ ...prev, pageName: e.target.value }))}
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Açıklama"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.pageName.trim()}
          >
            {selectedPage ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Silmeyi Onayla</DialogTitle>
        <DialogContent>
          <Typography>
            "{selectedPage?.pageName}" sayfasını silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageManagement;
